import React, { useState, useEffect, useCallback, useRef } from 'react';
import { usePageLayout } from '../../../../assets/contexts/PageLayoutContext';
import { useTheme } from '../../../../assets/contexts/ThemeContext';
import Http from '../../../../core/Http';

/** 페이지 전용 스타일 (기존 HeaderAreaManager.css 이관) */
const HEADER_AREA_MANAGER_PAGE_CSS = `
.header-area-manager-page .btn-icon-square {
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  max-width: 28px !important;
  min-height: 28px !important;
  max-height: 28px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  box-sizing: border-box;
  line-height: 1 !important;
  flex-shrink: 0;
  flex-grow: 0;
}
.header-area-manager-page .btn-icon-square svg {
  flex-shrink: 0;
}
`.trim();

/** 상단 배지 기본값 (DB 미구성 시·편집기 초기값 — RISE 사업, 한국외국어대학교) */
export function getDefaultHeaderBadges() {
  return [
    {
      icon: '🚀',
      text: 'RISE 사업',
      textEn: 'RISE',
      backgroundColor: '#e3f2fd',
      borderColor: '#90caf9',
      color: '#1565c0'
    },
    {
      icon: 'ti-world',
      text: '한국외국어대학교',
      textEn: 'Hankuk University of Foreign Studies',
      backgroundColor: '#e8f5e9',
      borderColor: '#81c784',
      color: '#2e7d32'
    }
  ];
}

/** 로고 태그 기본값 (HeaderBottom headerLogo.type === 'tag' 구조와 동일) */
export function getDefaultHeaderLogo() {
  return {
    type: 'tag',
    tag: {
      logo: {
        size: 49,
        backgroundColor: 'rgb(147, 51, 234)',
        color: '#ffffff',
        fontSize: '1.5rem',
        letter: 'G'
      },
      title: { kor: 'GLOW Platform', eng: 'GLOW Platform' },
      titleColor: '#6b21a8',
      titleStyle: { fontSize: '20px', fontWeight: 700, lineHeight: 1.2, letterSpacing: '0', textTransform: 'none' },
      subtitle: { kor: '지산학 R&D 플랫폼', eng: 'Industry-Academia R&D Platform' },
      subtitleColor: '#6b7280',
      subtitleStyle: { fontSize: '14px', fontWeight: 400, lineHeight: 1.5, letterSpacing: '0', textTransform: 'none' }
    }
  };
}

export function getDefaultHeaderAreaData() {
  return {
    badges: getDefaultHeaderBadges(),
    logo: getDefaultHeaderLogo()
  };
}

/**
 * API/폴백 문서(doc)를 HeaderBottom·HeaderTop이 쓰는 headerData 형태로 병합
 * @param {object|null} doc — { headerContent: { headerData } } 또는 null
 */
export function mergeHeaderDocumentToHeaderData(doc) {
  const defaults = getDefaultHeaderAreaData();
  const apiHd = doc?.headerContent?.headerData;
  if (!apiHd || typeof apiHd !== 'object') return defaults;

  const next = { ...defaults, ...apiHd };
  if (!Array.isArray(apiHd.badges)) {
    next.badges = defaults.badges;
  }
  if (!apiHd.logo || typeof apiHd.logo !== 'object') {
    next.logo = defaults.logo;
  } else if (apiHd.logo.type === 'image') {
    next.logo = { ...apiHd.logo };
  } else {
    const dt = defaults.logo?.tag || {};
    const at = apiHd.logo.tag || {};
    next.logo = {
      type: 'tag',
      tag: {
        ...dt,
        ...at,
        logo: { ...dt.logo, ...(at.logo || {}) },
        title: at.title ?? dt.title,
        subtitle: at.subtitle ?? dt.subtitle,
        titleStyle: { ...dt.titleStyle, ...at.titleStyle },
        subtitleStyle: { ...dt.subtitleStyle, ...at.subtitleStyle }
      }
    };
  }
  return next;
}

export function applyHeaderDataToWindowAndTheme(headerData, setTheme) {
  if (typeof window === 'undefined') return;
  window.headerContent = window.headerContent || {};
  window.headerContent.headerData = headerData;
  if (setTheme && headerData?.logo) {
    setTheme('headerLogo', headerData.logo);
  }
  window.dispatchEvent(new CustomEvent('glow-header-content-updated', { detail: headerData }));
}

const DeleteIconX = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const AddIconPlus = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M12 5v14" />
  </svg>
);

/** 체크 아이콘 (NTIS/IRIS 등) */
const CheckIcon = () => (
  <i className="ti-check" style={{ fontSize: '11px' }} aria-hidden="true" />
);

/**
 * CSS 색상 문자열을 type="color"용 hex(#RRGGBB)로 변환.
 * hex, rgb(), 그라데이션 첫 번째 색상 추출 지원.
 */
const toHexForColorInput = (cssColor) => {
  if (!cssColor || typeof cssColor !== 'string') return '#9333ea';
  const s = cssColor.trim();
  const hex6 = /^#([0-9A-Fa-f]{6})$/.exec(s);
  if (hex6) return s;
  const hex3 = /^#([0-9A-Fa-f]{3})$/.exec(s);
  if (hex3) {
    const r = hex3[1][0] + hex3[1][0], g = hex3[1][1] + hex3[1][1], b = hex3[1][2] + hex3[1][2];
    return '#' + r + g + b;
  }
  const rgb = /rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/.exec(s);
  if (rgb) {
    const r = Number(rgb[1]).toString(16).padStart(2, '0');
    const g = Number(rgb[2]).toString(16).padStart(2, '0');
    const b = Number(rgb[3]).toString(16).padStart(2, '0');
    return '#' + r + g + b;
  }
  const fromGradientHex = /#([0-9A-Fa-f]{6})/.exec(s);
  if (fromGradientHex) return '#' + fromGradientHex[1];
  const fromGradientRgb = /rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/.exec(s);
  if (fromGradientRgb) {
    const r = Number(fromGradientRgb[1]).toString(16).padStart(2, '0');
    const g = Number(fromGradientRgb[2]).toString(16).padStart(2, '0');
    const b = Number(fromGradientRgb[3]).toString(16).padStart(2, '0');
    return '#' + r + g + b;
  }
  return '#9333ea';
};

/**
 * Header 영역 관리 페이지
 * workplace_header_config JSON 저장·로드 (배지 + 좌측 로고 태그). 저장 시 window·테마·glow-header-config-saved 반영.
 */
const HeaderAreaManager = () => {
  const { setBannerData } = usePageLayout();
  const themeContext = useTheme();
  const themeConfig = themeContext?.themeConfig || (typeof window !== 'undefined' && window.__themeConfig) || {};
  const setTheme = themeContext?.set;

  useEffect(() => {
    setBannerData({
      title: 'Header 영역 관리',
      breadcrumbPath: '시스템 관리 > Header 영역 관리',
      loading: false
    });
    return () => setBannerData(null);
  }, [setBannerData]);

  const documentMetaRef = useRef({ schemaVersion: 1, headerId: null });
  const [data, setData] = useState(() => getDefaultHeaderAreaData());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessageState] = useState(null);

  const workplaceId = Number(themeConfig.workspaceId || (typeof window !== 'undefined' && window.__themeConfig?.workspaceId)) || 1;

  useEffect(() => {
    let cancelled = false;
    const wid = workplaceId;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const row = await Http.get(`/api/system/header-config/workspace/${wid}/latest`);
        if (cancelled) return;
        if (!row?.headerData) {
          setData(getDefaultHeaderAreaData());
          documentMetaRef.current = { schemaVersion: 1, headerId: row?.headerId ?? null };
          return;
        }
        const inner = typeof row.headerData === 'string' ? JSON.parse(row.headerData) : row.headerData;
        documentMetaRef.current = {
          schemaVersion: inner?.schemaVersion ?? 1,
          headerId: row.headerId ?? null
        };
        const merged = mergeHeaderDocumentToHeaderData(inner);
        setData(merged);
      } catch (e) {
        const status = e?.response?.status;
        if (!cancelled) {
          setData(getDefaultHeaderAreaData());
          documentMetaRef.current = { schemaVersion: 1, headerId: null };
        }
        if (status === 204 || status === 404) {
          if (!cancelled) setLoadError(null);
        } else if (!cancelled) {
          const detail = e?.response?.data?.message || e?.message || '';
          setLoadError(
            `workplace_header_config 테이블에서 최신 헤더 설정을 읽지 못했습니다.${detail ? ` — ${detail}` : ''}`
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workplaceId]);

  const updateData = useCallback((path, value) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        const key = parts[i];
        if (!(key in cur)) cur[key] = {};
        cur = cur[key];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  }, []);

  const addBadge = () => {
    setData((prev) => ({
      ...prev,
      badges: [...(prev.badges || []), { icon: '🚀', text: '', textEn: '', backgroundColor: '#e3f2fd', borderColor: '#90caf9', color: '#1565c0' }]
    }));
  };

  const removeBadge = (index) => {
    setData((prev) => ({
      ...prev,
      badges: (prev.badges || []).filter((_, i) => i !== index)
    }));
  };

  const setBadge = (index, field, value) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next.badges) next.badges = [];
      if (next.badges[index]) next.badges[index][field] = value;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessageState(null);
    try {
      const toSave = { ...data };
      toSave.logo = { type: 'tag', tag: toSave.logo?.tag || getDefaultHeaderLogo().tag };
      const wid = workplaceId;
      const meta = documentMetaRef.current;
      const doc = {
        schemaVersion: meta.schemaVersion ?? 1,
        headerContent: { headerData: toSave }
      };
      const headerDataJson = JSON.stringify(doc);
      await Http.post(`/api/system/header-config/workspace/${wid}`, { headerData: headerDataJson });
      applyHeaderDataToWindowAndTheme(toSave, setTheme);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('glow-header-config-saved', { detail: { workplaceId: wid } }));
      }
      setData(toSave);
      setSaveMessageState('저장되었습니다. DB에 반영되었고 헤더에 적용되었습니다.');
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '저장 중 오류가 발생했습니다.';
      setSaveMessageState(msg);
    } finally {
      setSaving(false);
    }
  };

  const sectionHeaderStyle = {
    borderLeft: '4px solid var(--theme-color-primary, #e97730)',
    paddingLeft: '12px',
    marginBottom: '0.75rem',
    fontSize: '1rem',
    fontWeight: '600'
  };

  const badges = data.badges || getDefaultHeaderBadges();
  const defaultLogo = getDefaultHeaderLogo();
  const tag = { ...defaultLogo.tag, ...data.logo?.tag };
  const logoStyle = { ...defaultLogo.tag?.logo, ...data.logo?.tag?.logo };

  return (
    <div className="container-fluid header-area-manager-page">
      <style>{HEADER_AREA_MANAGER_PAGE_CSS}</style>
      {loading && (
        <div className="alert alert-secondary py-2 mb-3" role="status">
          workplace_header_config 에서 헤더 설정을 불러오는 중…
        </div>
      )}
      {loadError && (
        <div className="alert alert-danger py-2 mb-3" role="alert">
          <strong>DB 조회 오류:</strong> {loadError}
        </div>
      )}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <span className="text-muted small">
          데이터 소스: <code>workplace_header_config</code> (workplaceId={workplaceId})
        </span>
        <div className="d-flex align-items-center gap-2">
          <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || loading}>
            {saving ? '저장 중...' : '전체 저장'}
          </button>
          {saveMessage && <span className="text-info small">{saveMessage}</span>}
        </div>
      </div>
      {/* 배지 영역 */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="fw-bold mb-0" style={sectionHeaderStyle}>배지 영역 (행 추가/삭제)</h6>
        <div className="d-flex align-items-center gap-2">
          <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || loading}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-lg-4">
              <div className="small text-muted mb-1">미리보기</div>
              <div
                className="rounded-3 p-3 d-flex flex-wrap align-items-center gap-2"
                style={{ backgroundColor: '#f8f8f8', border: '1px solid #eff1f3', minHeight: '56px' }}
              >
                {badges.map((b, i) => (
                  <div
                    key={i}
                    className="d-inline-flex align-items-center gap-1"
                    style={{
                      padding: '4px 10px',
                      backgroundColor: b.backgroundColor || '#e3f2fd',
                      border: `1px solid ${b.borderColor || '#90caf9'}`,
                      borderRadius: '16px',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: b.color || '#1565c0',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {b.icon === 'check' ? (
                      <CheckIcon />
                    ) : typeof b.icon === 'string' && b.icon.startsWith('ti-') ? (
                      <i className={b.icon} style={{ fontSize: '11px' }} aria-hidden="true" />
                    ) : (
                      <span style={{ fontSize: '12px' }}>{b.icon || '🚀'}</span>
                    )}
                    <span>{b.text || '(배지)'}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-12 col-lg-8">
              <label className="form-label small text-muted mb-0">배지 목록 (행 추가/삭제)</label>
              {badges.map((b, bi) => (
                <div key={bi} className="card border-1 border-gray-300 mb-2 position-relative">
                  <div className="card-body py-2 px-3">
                    {/* 1행: 항목 N + 엑스 버튼 */}
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="small fw-semibold text-secondary">항목 {bi + 1}</span>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm btn-icon-square"
                        onClick={() => removeBadge(bi)}
                        title="행 삭제"
                        aria-label="행 삭제"
                      >
                        <DeleteIconX />
                      </button>
                    </div>
                    {/* 2행: 아이콘, 배경색, 테두리색, 글자색 4컬럼 */}
                    <div className="row g-2 mb-2 align-items-end">
                      <div className="col-6 col-md-3">
                        <label className="form-label small text-muted mb-0">아이콘(이모지)</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="🚀 또는 check"
                          value={b.icon ?? ''}
                          onChange={(e) => setBadge(bi, 'icon', e.target.value)}
                        />
                      </div>
                      <div className="col-6 col-md-3">
                        <label className="form-label small text-muted mb-0">배경색</label>
                        <input
                          type="color"
                          className="form-control form-control-color form-control-sm p-1"
                          style={{ width: '2.5rem', height: '1.75rem', cursor: 'pointer' }}
                          value={/^#[0-9A-Fa-f]{6}$/.test(b.backgroundColor) ? b.backgroundColor : '#e3f2fd'}
                          onChange={(e) => setBadge(bi, 'backgroundColor', e.target.value)}
                          title="배경색"
                        />
                      </div>
                      <div className="col-6 col-md-3">
                        <label className="form-label small text-muted mb-0">테두리색</label>
                        <input
                          type="color"
                          className="form-control form-control-color form-control-sm p-1"
                          style={{ width: '2.5rem', height: '1.75rem', cursor: 'pointer' }}
                          value={/^#[0-9A-Fa-f]{6}$/.test(b.borderColor) ? b.borderColor : '#90caf9'}
                          onChange={(e) => setBadge(bi, 'borderColor', e.target.value)}
                          title="테두리색"
                        />
                      </div>
                      <div className="col-6 col-md-3">
                        <label className="form-label small text-muted mb-0">글자색</label>
                        <input
                          type="color"
                          className="form-control form-control-color form-control-sm p-1"
                          style={{ width: '2.5rem', height: '1.75rem', cursor: 'pointer' }}
                          value={/^#[0-9A-Fa-f]{6}$/.test(b.color) ? b.color : '#1565c0'}
                          onChange={(e) => setBadge(bi, 'color', e.target.value)}
                          title="글자색"
                        />
                      </div>
                    </div>
                    {/* 3행: 제목(한글), 제목(영문) 2컬럼 */}
                    <div className="row g-2">
                      <div className="col-6">
                        <label className="form-label small text-muted mb-0">제목 (한글)</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="배지 텍스트 (한글)"
                          value={b.text ?? ''}
                          onChange={(e) => setBadge(bi, 'text', e.target.value)}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label small text-muted mb-0">제목 (영문)</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="배지 텍스트 (영문)"
                          value={b.textEn ?? ''}
                          onChange={(e) => setBadge(bi, 'textEn', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <div className="d-flex justify-content-end mt-2">
                <button type="button" className="btn btn-outline-primary btn-sm btn-icon-square" onClick={addBadge} title="항목 추가" aria-label="항목 추가"><AddIconPlus /></button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 로고 영역 */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="fw-bold mb-0" style={sectionHeaderStyle}>로고 영역</h6>
        <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || loading}>
          {saving ? '저장 중...' : '저장'}
        </button>
      </div>
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-lg-4">
              <div className="small text-muted mb-1">미리보기</div>
              <div className="rounded-3 p-3 d-flex align-items-center gap-3" style={{ backgroundColor: '#fff', border: '1px solid #e9ecef', minHeight: '100px' }}>
                {/* 원형 로고: gradient는 background로, 단색은 backgroundColor로 적용해 항상 보이게 */}
                {(() => {
                  const size = Math.min(Number(logoStyle.size) || 49, 80) || 49;
                  const bg = (logoStyle.backgroundColor || '').toString().trim();
                  const isInvisible = !bg || size <= 0 || /^#?fff(f)?$/i.test(bg) || bg === 'white' || bg === 'transparent';
                  const circleBg = isInvisible ? 'rgb(147, 51, 234)' : (logoStyle.backgroundColor || 'rgb(147, 51, 234)');
                  const isGradient = circleBg.includes('gradient');
                  return (
                    <div
                      className="rounded-circle flex-shrink-0 d-flex align-items-center justify-content-center fw-bold"
                      style={{
                        width: `${size}px`,
                        height: `${size}px`,
                        ...(isGradient ? { background: circleBg } : { backgroundColor: circleBg }),
                        color: logoStyle.color || '#ffffff',
                        fontSize: logoStyle.fontSize || '1.5rem'
                      }}
                    >
                      {logoStyle.letter || 'G'}
                    </div>
                  );
                })()}
                <div>
                  <div style={{ fontSize: tag.titleStyle?.fontSize || '20px', fontWeight: 700, color: tag.titleColor || '#6b21a8', lineHeight: 1.2 }}>
                    {(typeof tag.title === 'object' && tag.title ? tag.title.kor : tag.title) || 'GLOW Platform'}
                  </div>
                  <div style={{ fontSize: tag.subtitleStyle?.fontSize || '14px', color: tag.subtitleColor || '#6b7280', marginTop: '2px' }}>
                    {(typeof tag.subtitle === 'object' && tag.subtitle ? tag.subtitle.kor : tag.subtitle) || '지산학 R&D 플랫폼'}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-8">
              <div className="row g-2 g-lg-3">
                {/* 로고: 모바일 100% → 5열 (로고:제목 = 5:7) */}
                <div className="col-12 col-lg-5">
                  <div className="border rounded p-3 h-100">
                    <label className="small fw-semibold text-secondary mb-2">로고</label>
                    <div className="d-flex flex-column gap-2">
                    <div className="row g-2">
                      <div className="col-12 col-sm-6">
                        <label className="form-label small text-muted mb-0">로고 글자</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="로고 글자"
                          value={logoStyle.letter ?? 'G'}
                          onChange={(e) => updateData('logo.tag.logo.letter', e.target.value)}
                        />
                      </div>
                      <div className="col-12 col-sm-6">
                        <label className="form-label small text-muted mb-0">크기(px)</label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          placeholder="크기(px)"
                          value={logoStyle.size ?? 49}
                          onChange={(e) => updateData('logo.tag.logo.size', Number(e.target.value) || 49)}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="form-label small text-muted mb-0">원형 배경색</label>
                      <input
                        type="color"
                        className="form-control form-control-color form-control-sm p-1 d-block mb-1"
                        style={{ width: '2.5rem', height: '1.75rem', cursor: 'pointer' }}
                        value={toHexForColorInput(logoStyle.backgroundColor)}
                        onChange={(e) => updateData('logo.tag.logo.backgroundColor', e.target.value)}
                        title="원형 배경색 (단색)"
                      />
                      <textarea
                        className="form-control form-control-sm"
                        placeholder="#hex, rgb(), linear-gradient(...)"
                        value={logoStyle.backgroundColor ?? ''}
                        onChange={(e) => updateData('logo.tag.logo.backgroundColor', e.target.value)}
                        title="원형 배경색 (최종 적용값)"
                        rows={4}
                        style={{ resize: 'vertical', minHeight: '2.5rem' }}
                      />
                    </div>
                  </div>
                  </div>
                </div>
                {/* 제목 · 부제목: 모바일 100% → 7열 (로고:제목 = 5:7) */}
                <div className="col-12 col-lg-7">
                <div className="border rounded p-3 mb-3">
                  <div className="small fw-semibold text-secondary mb-2">제목</div>
                  <div className="row g-2 align-items-end">
                    <div className="col col-md" style={{ minWidth: 0 }}>
                      <label className="form-label small text-muted mb-0">한글</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="제목 (예: GLOW Platform)"
                        value={typeof tag.title === 'object' && tag.title ? (tag.title.kor ?? '') : (tag.title ?? '')}
                        onChange={(e) => updateData('logo.tag.title', {
                          kor: e.target.value,
                          eng: (typeof tag.title === 'object' && tag.title ? tag.title.eng : tag.titleEn) ?? ''
                        })}
                      />
                    </div>
                    <div className="col-auto">
                      <label className="form-label small text-muted mb-0">색상</label>
                      <input
                        type="color"
                        className="form-control form-control-color form-control-sm p-1 d-block"
                        style={{ width: '2.5rem', height: '1.75rem', cursor: 'pointer' }}
                        value={toHexForColorInput(tag.titleColor)}
                        onChange={(e) => updateData('logo.tag.titleColor', e.target.value)}
                        title="제목 색상"
                      />
                    </div>
                  </div>
                  <div className="row g-2 align-items-end mt-2">
                    <div className="col-12">
                      <label className="form-label small text-muted mb-0">영문</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Title (e.g. GLOW Platform)"
                        value={typeof tag.title === 'object' && tag.title ? (tag.title.eng ?? '') : (tag.titleEn ?? '')}
                        onChange={(e) => updateData('logo.tag.title', {
                          kor: (typeof tag.title === 'object' && tag.title ? tag.title.kor : tag.title) ?? '',
                          eng: e.target.value
                        })}
                      />
                    </div>
                  </div>
                </div>
                <div className="border rounded p-3">
                  <div className="small fw-semibold text-secondary mb-2">부제목</div>
                  <div className="row g-2 align-items-end">
                    <div className="col col-md" style={{ minWidth: 0 }}>
                      <label className="form-label small text-muted mb-0">한글</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="부제목 (예: 지산학 R&D 플랫폼)"
                        value={typeof tag.subtitle === 'object' && tag.subtitle ? (tag.subtitle.kor ?? '') : (tag.subtitle ?? '')}
                        onChange={(e) => updateData('logo.tag.subtitle', {
                          kor: e.target.value,
                          eng: (typeof tag.subtitle === 'object' && tag.subtitle ? tag.subtitle.eng : tag.subtitleEn) ?? ''
                        })}
                      />
                    </div>
                    <div className="col-auto">
                      <label className="form-label small text-muted mb-0">색상</label>
                      <input
                        type="color"
                        className="form-control form-control-color form-control-sm p-1 d-block"
                        style={{ width: '2.5rem', height: '1.75rem', cursor: 'pointer' }}
                        value={toHexForColorInput(tag.subtitleColor)}
                        onChange={(e) => updateData('logo.tag.subtitleColor', e.target.value)}
                        title="부제목 색상"
                      />
                    </div>
                  </div>
                  <div className="row g-2 align-items-end mt-2">
                    <div className="col-12">
                      <label className="form-label small text-muted mb-0">영문</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Subtitle (e.g. Industry-Academia R&D Platform)"
                        value={typeof tag.subtitle === 'object' && tag.subtitle ? (tag.subtitle.eng ?? '') : (tag.subtitleEn ?? '')}
                        onChange={(e) => updateData('logo.tag.subtitle', {
                          kor: (typeof tag.subtitle === 'object' && tag.subtitle ? tag.subtitle.kor : tag.subtitle) ?? '',
                          eng: e.target.value
                        })}
                      />
                    </div>
                  </div>
                </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeaderAreaManager;
